import { ColorSchemeScript, MantineProvider } from "@mantine/core";
import { Notifications as Toasts } from "@mantine/notifications";
import { NavigationProgress, nprogress } from "@mantine/nprogress";
import { json, LoaderFunctionArgs } from "@remix-run/node";
import {
  ClientLoaderFunctionArgs,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useNavigation,
  useRouteError,
} from "@remix-run/react";
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import { Provider as JotaiProvider } from "jotai";
import { useEffect } from "react";
import { cacheClientLoader, useCachedLoaderData } from "remix-client-cache";

import "@mantine/charts/styles.css";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/nprogress/styles.css";
import "@xyflow/react/dist/style.css";

import { useToast } from "~/components/toast";
import { theme } from "~/lib/mantine-theme";
import { getToast } from "~/lib/toast.server";
import { combineHeaders } from "~/lib/utils";

import "leaflet/dist/leaflet.css";
import "~/styles/tailwind.css";
import ModalConfirm from "./components/modal/modal-confirm";
import ModalConfirmation from "./components/modal/modal-confirmation";
import ModalDeleteGlobal from "./components/modal/modal-delete-global";
import { ImpersonationBanner } from "./components/impersonation/impersonation-banner";
import { requireAuth } from "./lib/auth";
import { IMPERSONATION_TARGET_NAME, IS_IMPERSONATING, getSession } from "./session.server";

type SessionProfile = {
  employeeId: string | null;
  roleId: string | null;
  companyId: string | null;
  companySlug: string | null;
  companyLogoUrl: string | null;
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const accessToken = await requireAuth(request);

  const { toast, headers: toastHeaders } = await getToast(request);
  const session = await getSession(request);

  const sessionProfile: SessionProfile = {
    employeeId: session.get("employeeId"),
    roleId: session.get("roleId"),
    companyId: session.get("companyId"),
    companyLogoUrl: session.get("companyLogoUrl"),
    companySlug: session.get("companySlug"),
  };

  const isImpersonating = session.get(IS_IMPERSONATING) === true;
  const impersonationTargetName =
    (session.get(IMPERSONATION_TARGET_NAME) as string) || null;

  if (!accessToken) {
    return json(
      {
        toast,
        sessionProfile,
        notificationCount: 0,
        isImpersonating: false,
        impersonationTargetName: null,
      },
      {
        headers: combineHeaders(toastHeaders),
      },
    );
  }

  return json(
    {
      toast,
      sessionProfile,
      isImpersonating,
      impersonationTargetName,
    },
    {
      headers: combineHeaders(toastHeaders),
    },
  );
};

export const clientLoader = (args: ClientLoaderFunctionArgs) =>
  cacheClientLoader(args, {
    key: "user-profile",
  });

clientLoader.hydrate = true;

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return <div>Something went wrong</div>;
};

function App() {
  const {
    toast: toastData,
    isImpersonating,
    impersonationTargetName,
  } = useCachedLoaderData<typeof loader>();

  useToast(toastData);

  const navigation = useNavigation();

  useEffect(() => {
    if (navigation.state === "loading") {
      nprogress.start();
    } else {
      nprogress.complete();
    }
  }, [navigation.state]);

  return (
    <html lang="en" className="h-full">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={`/apple-touch-icon.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="96x96"
          href={`/favicon-96x96.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={`/favicon-32x32.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={`/favicon-16x16.png`}
        />
        <link rel="manifest" href="/site.webmanifest" />
        <ColorSchemeScript />
      </head>
      <body className="h-full bg-[#F8F9FA]">
        <MantineProvider theme={theme}>
          <NavigationProgress color="gray" />
          <JotaiProvider>
            <ModalConfirmation />
            <ModalDeleteGlobal />
            <ModalConfirm />
            <Outlet />
            {isImpersonating && (
              <ImpersonationBanner targetName={impersonationTargetName} />
            )}
            <ScrollRestoration />
            <Scripts />
            <Toasts />
          </JotaiProvider>
        </MantineProvider>
      </body>
    </html>
  );
}

export default withSentry(App);
